import React, { useState } from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { contactApi } from "../lib/api";
import { toast } from "sonner";

const FORM_TYPES = [
  ["contact",      "General Contact"],
  ["request_demo", "Request Demo"],
  ["pilot",        "Pilot Program Interest"],
  ["gov_agency",   "Government Agency Inquiry"],
  ["data_center",  "Data Center Infrastructure Inquiry"],
  ["cyber_assess", "Cybersecurity Assessment"],
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "", organization: "", role: "", email: "", phone: "",
    website: "", use_case: "", message: "", form_type: "contact",
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await contactApi.submit(form);
      toast.success(r.message || "Thanks — we'll be in touch.");
      setForm({ ...form, name: "", organization: "", role: "", email: "", phone: "", website: "", use_case: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    }
    setBusy(false);
  };

  return (
    <Layout>
      <SEOHead title="Contact & Request Demo | AuditIQ" path="/contact"
        description="Request an AuditIQ demo, pilot program, or CyberWater assessment. Built for cities, counties, data centers, and utilities." />
      <PageHeader eyebrow="Contact" title="Tell us what you're trying to govern."
        subtitle="Short on time? Request a demo. We'll follow up with a 30-minute walkthrough matched to your entity type." />
      <section className="max-w-[1100px] mx-auto px-6 py-16">
        <form onSubmit={submit} className="panel-elev p-8 grid md:grid-cols-2 gap-5" data-testid="contact-form">
          <Field label="Full name" required>
            <Input required value={form.name} onChange={set("name")}
              data-testid="contact-name" className="bg-navy-deep border-gold-dim/40" />
          </Field>
          <Field label="Work email" required>
            <Input required type="email" value={form.email} onChange={set("email")}
              data-testid="contact-email" className="bg-navy-deep border-gold-dim/40" />
          </Field>
          <Field label="Organization">
            <Input value={form.organization} onChange={set("organization")}
              data-testid="contact-org" className="bg-navy-deep border-gold-dim/40" />
          </Field>
          <Field label="Role / Title">
            <Input value={form.role} onChange={set("role")}
              data-testid="contact-role" className="bg-navy-deep border-gold-dim/40" />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={set("phone")}
              data-testid="contact-phone" className="bg-navy-deep border-gold-dim/40" />
          </Field>
          <Field label="Website">
            <Input value={form.website} onChange={set("website")}
              data-testid="contact-website" className="bg-navy-deep border-gold-dim/40" />
          </Field>
          <Field label="Use case">
            <Input value={form.use_case} onChange={set("use_case")}
              placeholder="e.g. budget transparency, CyberWater pilot"
              data-testid="contact-usecase" className="bg-navy-deep border-gold-dim/40" />
          </Field>
          <Field label="Form type">
            <Select value={form.form_type} onValueChange={set("form_type")}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="contact-formtype">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {FORM_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Message">
              <Textarea rows={5} value={form.message} onChange={set("message")}
                data-testid="contact-message" className="bg-navy-deep border-gold-dim/40" />
            </Field>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button disabled={busy} className="btn-gold h-12 px-8" data-testid="contact-submit">
              {busy ? "Sending…" : "Send Inquiry"}
            </Button>
          </div>
        </form>
      </section>
    </Layout>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.22em] text-steel">
      {label}{required && <span className="text-gold ml-1">*</span>}
    </label>
    <div className="mt-1">{children}</div>
  </div>
);

export default Contact;
